import React, { useState, useEffect } from 'react';
import './left_bar.css';
import RadioList from './RadioList.tsx';
import { useLanguage } from './context/LanguageContext.tsx';

const LeftBar = ({ onFilterChange, checkedItems, onThresholdChange, onBandChange, onYearChange, onDaysChange, onProductChange, selectedYear }) => {
    const { language } = useLanguage();
    const translations = {
        cz: {
            method: 'Metoda',
            settings: 'Nastavení',
            selectMethod: 'Vyberte metodu',
            seasonStart: 'Začátek vegetační sezony',
            seasonEnd: 'Konec vegetační sezony',
            seasonLength: 'Délka vegetační sezony',
            tempThreshold: 'Hraniční teplota:',
            daysAbove: 'Počet dní nad x°C',
            daysBelow: 'Počet dní pod x°C',
            daysAround: 'Počet dní nad a pod x°C',
            year: 'Rok:',
            daysOptions: ['5 dní', '10 dní', '15 dní'],
            productOptions: [
                { label: 'Kombinovaný produkt', value: 'combinedProduct' },
                { label: 'Dálkový průzkum Země', value: 'dpz' },
                { label: 'Meteorologická data', value: 'meteorologicalData' },
                { label: 'PAR Senzory', value: 'parSensors' },
            ],
            velicinaOptions: [
                { label: 'Začátek vegetační sezony', value: 'vegetationSeason' },
                { label: 'Konec vegetační sezony', value: 'vegetationSeasonEnd' },
                { label: 'Délka vegetační sezony', value: 'seasonLength' },
            ],
            velicinaInfoTexts: {
                vegetationSeason: 'Začátek vegetační sezony je považován za období, kdy určitá denní teplota vzduchu překročí konkrétní prahovou hodnotu (např. 5, 10, 15 °C) zpravidla pět dní po sobě.',
                vegetationSeasonEnd: 'Konec vegetační sezony je považován za období, kdy určitá denní teplota vzduchu klesne pod konkrétní hodnotu (např. 5, 10, 15 °C) několik dní po sobě.',
                seasonLength: 'Délka vegetační sezony je celkový počet dnů s charakteristickou průměrnou denní teplotou vzduchu.',
            },
        },
        en: {
            method: 'Method',
            settings: 'Settings',
            selectMethod: 'Select method',
            seasonStart: 'Start of the vegetation season',
            seasonEnd: 'End of the vegetation season',
            seasonLength: 'Length of the vegetation season',
            tempThreshold: 'Temperature threshold:',
            daysAbove: 'Days above x°C',
            daysBelow: 'Days below x°C',
            daysAround: 'Days above and below x°C',
            year: 'Year:',
            daysOptions: ['5 days', '10 days', '15 days'],
            productOptions: [
                { label: 'Combined product', value: 'combinedProduct' },
                { label: 'Remote Sensing', value: 'dpz' },
                { label: 'Meteorological data', value: 'meteorologicalData' },
                { label: 'PAR Sensors', value: 'parSensors' },
            ],
            velicinaOptions: [
                { label: 'Start of the vegetation season', value: 'vegetationSeason' },
                { label: 'End of the vegetation season', value: 'vegetationSeasonEnd' },
                { label: 'Length of the vegetation season', value: 'seasonLength' },
            ],
            velicinaInfoTexts: {
                vegetationSeason: 'Start of the vegetation season is when daily temperature exceeds a threshold (e.g., 5, 10, 15 °C) for several days in a row.',
                vegetationSeasonEnd: 'End of the vegetation season is when daily temperature drops below a threshold for several days in a row.',
                seasonLength: 'Total number of days with characteristic daily average temperature.',
            },
        }
    }[language];
    
    const velicinaOptions = [
        { label: translations.seasonStart, value: "vegetationSeason" },
        { label: translations.seasonEnd, value: "vegetationSeasonEnd" },
        { label: translations.seasonLength, value: "seasonLength" },
    ];

    const productOptions = translations.productOptions;

    const [selectedVelicina, setSelectedVelicina] = useState<string | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<string>("combinedProduct");

    useEffect(() => {
        const today = new Date();
        const month = today.getMonth() + 1;
        const day = today.getDate();

        const defaultValue = "vegetationSeason";

        setSelectedVelicina(defaultValue);
        onFilterChange({
            vegetationSeason: defaultValue === "vegetationSeason",
            vegetationSeasonEnd: false,
            seasonLength: false,
        });
    }, []);

    const velicinaInfoTexts = {
        vegetationSeason: translations.velicinaInfoTexts.vegetationSeason,
        vegetationSeasonEnd: translations.velicinaInfoTexts.vegetationSeasonEnd,
        seasonLength: translations.velicinaInfoTexts.seasonLength,
    };

    const handleVelicinaChange = (value: string) => {
        setSelectedVelicina((prevSelected) => (prevSelected === value ? null : value));

        const updatedItems = { vegetationSeason: false, vegetationSeasonEnd: false, seasonLength: false };

        if (selectedVelicina !== value) {
            updatedItems[value as keyof typeof updatedItems] = true;
        }

        onFilterChange(updatedItems);
    };

    const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = e.target.value;
        setSelectedProduct(selected);
        onProductChange(selected);
    };


    return (
        <div className="left-bar">
            <div className="left-bar__outputs">
                <div className="left-bar__title-area">
                    <h2 className="left-bar__title">{translations.method}</h2>
                </div>
                <div className="left-bar__product-wrapper">
                    <div className="left-bar__product-area">
                        <select
                            id="product-select"
                            value={selectedProduct}
                            onChange={handleProductChange}
                            className="left-bar__dropdown--large-arrow"
                        >
                            {productOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="left-bar__options-area">
                    <RadioList
                        options={velicinaOptions}
                        selectedValue={selectedVelicina}
                        onSelectionChange={handleVelicinaChange}
                        groupName="velicina-options"
                        infoTexts={velicinaInfoTexts}
                    />
                </div>
            </div>

            <div className="left-bar__settings">
                <div className="left-bar__title-area">
                    <h2 className="left-bar__title">{translations.settings}</h2>
                </div>

                <div className="left-bar__season-area">
                    <h3 className="left-bar__subtitle">
                        {selectedVelicina === "vegetationSeason"
                            ? translations.seasonStart
                            : selectedVelicina === "vegetationSeasonEnd"
                                ? translations.seasonEnd
                                : selectedVelicina === "seasonLength"
                                    ? translations.seasonLength
                                    : translations.selectMethod}
                    </h3>
                </div>

                {/* Year selector for DPZ product */}
                {selectedProduct === "dpz" &&
                    ["vegetationSeason", "vegetationSeasonEnd", "seasonLength"].includes(selectedVelicina || "") && (
                        <div className="left-bar__temperature-select">
                            <div className="dropdown-row">
                                <label htmlFor="year">{translations.year}</label>
                                <select
                                    id="year"
                                    className="left-bar__dropdown"
                                    value={selectedYear}
                                    onChange={(e) => onYearChange(Number(e.target.value))}
                                >
                                    {[2020, 2021, 2022, 2023, 2024, 2025, 2026].map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                {/* Year selector for combined product */}
                {selectedProduct === "combinedProduct" &&
                    ["vegetationSeason", "vegetationSeasonEnd", "seasonLength"].includes(selectedVelicina || "") && (
                        <div className="left-bar__temperature-select">
                            <div className="dropdown-row">
                                <label htmlFor="year">{translations.year}</label>
                                <select
                                    id="year"
                                    className="left-bar__dropdown"
                                    value={selectedYear}
                                    onChange={(e) => onYearChange(Number(e.target.value))}
                                >
                                    {[2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026].map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                {/* PAR sensors */}
                {selectedProduct === "parSensors" &&
                    ["vegetationSeason", "vegetationSeasonEnd", "seasonLength"].includes(selectedVelicina || "") && (
                        <div className="left-bar__temperature-select">
                            <div className="dropdown-row">
                                <label htmlFor="year">Rok:</label>
                                <select
                                    id="year"
                                    className="left-bar__dropdown"
                                    value={selectedYear} // ✅ Controlled input
                                    onChange={(e) => onYearChange(Number(e.target.value))}
                                >
                                    <option value={2024}>2024</option>
                                    <option value={2025}>2025</option>
                                    <option value={2026}>2026</option>
                                </select>
                            </div>
                        </div>
                    )}

                {/* Meteorological product settings */}
                {selectedProduct === "meteorologicalData" &&
                    ["vegetationSeason", "vegetationSeasonEnd", "seasonLength"].includes(selectedVelicina || "") && (
                        <div className="left-bar__temperature-select">
                            <div className="dropdown-row">
                                <label htmlFor="temperature-threshold">{translations.tempThreshold}</label>
                                <select
                                    id="temperature-threshold"
                                    className="left-bar__dropdown"
                                    onChange={(e) => onThresholdChange(Number(e.target.value))}
                                >
                                    <option value={5}>5°C</option>
                                    <option value={10}>10°C</option>
                                    <option value={15}>15°C</option>
                                </select>
                            </div>
                            <div className="dropdown-row">
                                <label htmlFor="days-above-threshold">
                                    {selectedVelicina === "vegetationSeason"
                                        ? translations.daysAbove
                                        : selectedVelicina === "vegetationSeasonEnd"
                                            ? translations.daysBelow
                                            : translations.daysAround}
                                </label>
                                <select
                                    id="days-above-threshold"
                                    className="left-bar__dropdown"
                                    onChange={(e) => onDaysChange(Number(e.target.value))}
                                >
                                    <option value={5}>5 dní</option>
                                    <option value={10}>10 dní</option>
                                    <option value={15}>15 dní</option>
                                </select>
                            </div>
                            <div className="dropdown-row">
                                <label htmlFor="year">{translations.year}</label>
                                <select
                                    id="year"
                                    className="left-bar__dropdown"
                                    value={selectedYear} // ✅ Controlled input
                                    onChange={(e) => onYearChange(Number(e.target.value))}
                                >
                                    {[2022, 2023, 2024, 2025, 2026].map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
};

export default LeftBar;
